import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Center,
  Heading,
  Link,
  ListItem,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import moment from 'moment';

import {
  QUERY_DOCUMENTS_BY_TYPE,
} from '../../Queries';

import { useUser } from '../../Context';
import { LoginModal } from '../../Components';

const DOCUMENT_TABS = [
  { label: 'Board Minutes', type: 'BOARD_MINUTES' },
  { label: 'Financial Statements', type: 'FINANCIAL_STATEMENTS' },
  { label: 'Membership Materials', type: 'MEMBERSHIP_MATERIALS' },
  { label: 'Other Documents', type: 'OTHER_DOCUMENTS' },
];

const DocumentList = ({ documentType }) => {
  const { loading, data, error } = useQuery(QUERY_DOCUMENTS_BY_TYPE, {
    variables: { document_type: documentType },
    errorPolicy: 'all',
  });

  if (loading) {
    return (
      <Center py={8}>
        <Spinner />
      </Center>
    );
  }

  if (error) {
    return <Text color="red.500">Error loading documents: {error.message}</Text>;
  }

  const documents = data?.queryDocumentsByType?.newsletters;

  if (!documents || documents.length === 0) {
    return (
      <Text color="gray.500" py={4}>
        No documents available in this category yet.
      </Text>
    );
  }

  return (
    <UnorderedList spacing={2} mt={4}>
      {documents.map((doc) => (
        <ListItem key={doc.newsletter_id}>
          <Link href={doc.newsletter_details.newsletter_url} isExternal color="blue.500">
            {doc.newsletter_details.newsletter_name}
          </Link>
          {doc.newsletter_details.newsletter_date && (
            <Text as="span" color="gray.500" fontSize="sm" ml={2}>
              ({moment.utc(doc.newsletter_details.newsletter_date * 1000).format('MMMM YYYY')})
            </Text>
          )}
        </ListItem>
      ))}
    </UnorderedList>
  );
};

export const Newsletters = () => {
  const { user } = useUser();
  const { isOpen: isLoginOpen, onClose: onLoginClose, onOpen: onLoginOpen } = useDisclosure();

  if (!user) {
    return (
      <>
        <Text>
          <Button as="a" variant="link" onClick={onLoginOpen}>
            Login
          </Button>{' '}
          to view member documents.
        </Text>
        <LoginModal isOpen={isLoginOpen} onClose={onLoginClose} />
      </>
    );
  }

  return (
    <Box>
      <Heading as="h2" size="md" mb={4} color="black">
        Member Documents
      </Heading>
      <Text mb={4} color="gray.600" fontSize="sm">
        Documents shared with the membership by the Board of Directors.
      </Text>
      <Tabs colorScheme="teal" variant="enclosed">
        <TabList flexWrap="wrap">
          {DOCUMENT_TABS.map((tab) => (
            <Tab key={tab.type}>{tab.label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {DOCUMENT_TABS.map((tab) => (
            <TabPanel key={tab.type}>
              <DocumentList documentType={tab.type} />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
